import type {
  CustomerCounts,
  CustomerStatus,
  MaintenanceRenewal,
  RenewalStatus,
  SSOProvider,
} from '@central/api';
import { formatDay, type StatusTone } from '@central/ui';

/** The customer page's sections, in design order; Overview is the default. */
export const customerTabs = {
  overview: 'Overview',
  environments: 'Environments',
  licenses: 'Licenses',
  maintenance: 'Maintenance',
  users: 'Portal users',
  audit: 'Audit log',
} as const;

export type CustomerTab = keyof typeof customerTabs;

const countOf: Partial<Record<CustomerTab, keyof CustomerCounts>> = {
  environments: 'environments',
  licenses: 'licenses',
  users: 'portalUsers',
};

/** "Environments · 3"; the bare name for a section with no count or before counts load. */
export function tabLabel(tab: CustomerTab, counts?: CustomerCounts): string {
  const key = countOf[tab];
  return key && counts ? `${customerTabs[tab]} · ${counts[key]}` : customerTabs[tab];
}

export const renewalStatusLabel: Record<RenewalStatus, string> = {
  none: 'Not started',
  contacted: 'Contacted',
  quote_sent: 'Quote sent',
  renewed: 'Renewed',
  declined: 'Declined',
};

const renewalTone: Record<RenewalStatus, StatusTone> = {
  none: 'unk',
  contacted: 'blu',
  quote_sent: 'warn',
  renewed: 'ok',
  declined: 'down',
};

/** The Renewal row's chip: "Quote sent Oct 14"; "Not started" with no renewal or `none`. */
export function renewalChip(r: MaintenanceRenewal | undefined): {
  tone: StatusTone;
  label: string;
} {
  if (!r || r.status === 'none') return { tone: 'unk', label: renewalStatusLabel.none };
  return {
    tone: renewalTone[r.status],
    label: `${renewalStatusLabel[r.status]} ${formatDay(r.at)}`,
  };
}

const ssoKind = { oidc: 'OIDC', saml: 'SAML' } as const;

/**
 * The Organization SSO row: each enabled provider as "Entra ID (OIDC)";
 * "Off" when every provider is disabled, "Not set up" with none.
 */
export function ssoSummary(providers: readonly SSOProvider[]): string {
  if (providers.length === 0) return 'Not set up';
  const on = providers.filter((p) => p.enabled);
  if (on.length === 0) return 'Off';
  return on.map((p) => `${p.name} (${ssoKind[p.kind]})`).join(', ');
}

/** The status changes offered from each status, in menu order. */
export const statusMoves: Record<CustomerStatus, CustomerStatus[]> = {
  active: ['suspended', 'archived'],
  suspended: ['active', 'archived'],
  archived: ['active'],
};

export const statusMove: Record<CustomerStatus, { verb: string; effect: string }> = {
  active: {
    verb: 'Reactivate customer',
    effect:
      'The customer is marked active again; an archived customer is reached by announcements again.',
  },
  suspended: {
    verb: 'Suspend customer',
    effect:
      'Suspending marks the customer only: licenses, environments and portal sign-in are not changed. Suspend a license in the license editor to stop it.',
  },
  archived: {
    verb: 'Archive customer',
    effect:
      'An archived customer is left out of announcements, and crash reports are not filed to it. Licenses, environments and portal users are not changed.',
  },
};
